import React from "react";
import { Card, Flex, LoadingIndicator, SlideoutDrawerV2, Text, Link } from "@jsq/ds/core";
import { useGetPayeeQuery } from "../../../../graphql/supergraph.index";
import { clientContext } from "../../../../utils/graphql_utils";
import { ExistingPaymentPrefDisplay } from "../../../PaymentPrefConsolidated/Components/ExistingPaymentPrefDisplay";
import { DocumentTracker } from "../DocumentTracker";
import { PaymentDetailField } from "../PaymentDetailField";
import {
  slideoutContainer,
  leftColumn,
  rightColumn,
  slideoutBody,
  leftColumnWithBackground,
  rightColumnContent,
} from "./styles_vanilla.css";

interface PayeeDetailsProps {
  isOpen: boolean;
  onClose: () => void;
  payeeId: string | null;
}

export const PayeeDetails: React.FC<PayeeDetailsProps> = ({ isOpen, onClose, payeeId }) => {
  const { data, loading, error } = useGetPayeeQuery({
    variables: {
      payeeId: payeeId!,
    },
    skip: !payeeId,
    fetchPolicy: "cache-and-network",
    context: clientContext(),
  });

  const payee = data?.payee;
  const canRevealPaymentPref = Boolean(data?.currentUser?.canRevealPaymentPrefPermission);

  // Document-related variables
  const paymentPrefDocs = payee?.paymentPrefDocuments || [];
  const primaryDocument = paymentPrefDocs[0];
  const documentUrl = primaryDocument?.viewUrl || "";

  // Payee documents for hyperlink in right column
  const payeeDocs = payee?.payeeDocuments || [];
  const primaryPayeeDocument = payeeDocs[0];

  // Determine tax form label based on document type from query
  const getTaxFormLabel = () => {
    if (!primaryPayeeDocument) return "";

    switch (primaryPayeeDocument.documentType) {
      case "us_tax":
        return "W9 (US vendor)";
      case "non_us_tax":
        return "W8 (Non-US vendor)";
      default:
        return "Tax document";
    }
  };

  const taxFormLabel = getTaxFormLabel();

  if (!payeeId) {
    return null;
  }

  return (
    <SlideoutDrawerV2 isOpen={isOpen} onClose={onClose} data-testid="payee-details-slideout">
      <SlideoutDrawerV2.Body className={slideoutBody}>
        {loading && <LoadingIndicator />}
        {error && (
          <Text variant="regular-md" color="error">
            Error loading payee details: {error.message}
          </Text>
        )}
        {payee && (
          <Flex className={slideoutContainer}>
            <Flex className={`${leftColumn} ${leftColumnWithBackground}`}>
              {!primaryDocument ? (
                <Flex flexDirection="column" alignItems="center" justifyContent="center">
                  <Text variant="regular-md" color="muted">
                    No payment preference documents available for this payee.
                  </Text>
                </Flex>
              ) : (
                <DocumentTracker
                  documentName={primaryDocument.name}
                  documentUrl={documentUrl}
                  slideoutMode={true}
                />
              )}
            </Flex>

            <Flex className={rightColumn}>
              <SlideoutDrawerV2.ComplexHeader
                title={payee.name}
                subtitles={[
                  payee.payeeType === "vendor"
                    ? "Vendor"
                    : payee.payeeType === "entity"
                      ? "Entity"
                      : payee.payeeType === "investment"
                        ? "Investment"
                        : "Payee",
                ]}
              />

              <Flex flexDirection="column" className={rightColumnContent} gap={16}>
                <Card padding="lg" title="Client Details">
                  <PaymentDetailField label="Client" value={payee.arena?.name} />
                </Card>

                <Card padding="lg" title="Payee Details">
                  <Flex flexDirection="column" gap={16}>
                    <PaymentDetailField
                      label={
                        payee.payeeType === "vendor"
                          ? "Vendor"
                          : payee.payeeType === "entity"
                            ? "Entity"
                            : "Investment"
                      }
                      value={payee.name}
                    />

                    {payee.address && (
                      <Flex gap={8}>
                        <Flex flexBasis="40%">
                          <Text variant="secondary-md">Address</Text>
                        </Flex>
                        <Flex flexBasis="60%" flexDirection="column">
                          {[
                            payee.address.street,
                            payee.address.street2,
                            payee.address.street3,
                            payee.address.city,
                            payee.address.state?.name || payee.address.region,
                            payee.address.postalCode,
                            payee.address.country?.name,
                          ]
                            .filter(Boolean)
                            .map((line, index) => (
                              <Text key={index} variant="regular-md">
                                {line}
                              </Text>
                            ))}
                        </Flex>
                      </Flex>
                    )}

                    <PaymentDetailField label="Tax ID" value={payee.taxId ?? undefined} />

                    {/* Tax form information in two-row layout */}
                    <Flex flexDirection="column">
                      {/* Row 1: Tax form type */}
                      <Flex gap={8}>
                        <Flex flexBasis="40%">
                          <Text variant="secondary-md">Tax form</Text>
                        </Flex>
                        <Flex flexBasis="60%">
                          <Text variant="regular-md">{taxFormLabel}</Text>
                        </Flex>
                      </Flex>

                      {/* Row 2: Document link (only if document exists) */}
                      {primaryPayeeDocument && primaryPayeeDocument.viewUrl && (
                        <Flex gap={8}>
                          <Flex flexBasis="40%">{/* Empty left column */}</Flex>
                          <Flex flexBasis="60%">
                            <Link
                              href={primaryPayeeDocument.viewUrl}
                              shouldOpenInNewTab
                              align="left"
                              icon="external-link"
                              iconPosition="right"
                            >
                              {primaryPayeeDocument.name}
                            </Link>
                          </Flex>
                        </Flex>
                      )}
                    </Flex>

                    <PaymentDetailField
                      label="1099 Required"
                      value={
                        payee.form1099Required === true
                          ? "Yes"
                          : payee.form1099Required === false
                            ? "No"
                            : ""
                      }
                    />
                  </Flex>
                </Card>

                {payee.paymentPref && (
                  <Card padding="lg" title="Payment Instructions">
                    <ExistingPaymentPrefDisplay
                      paymentPref={payee.paymentPref}
                      displayRevealDetails={canRevealPaymentPref}
                    />
                  </Card>
                )}
              </Flex>
            </Flex>
          </Flex>
        )}
      </SlideoutDrawerV2.Body>
    </SlideoutDrawerV2>
  );
};

export default PayeeDetails;
